export const isIos = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = (navigator.userAgent || '').toLowerCase();
  const platform = (navigator as any).platform || '';

  if (/iphone|ipad|ipod/.test(ua)) return true;

  // iPadOS 13+ may report MacIntel; detect by presence of touch points
  if (platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1) return true;

  return false;
};

export const isInStandaloneMode = (): boolean => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  return (
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    !!(navigator as any).standalone
  );
};

export const isSafari = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = (navigator.userAgent || '').toLowerCase();
  const vendor = (navigator as any).vendor || '';

  // Quick exclusions for known non-Safari browsers
  if (/crios|chrome|android|edg|opr|samsungbrowser|fxios/.test(ua)) return false;
  if (!/apple/i.test(vendor) && !/safari/.test(ua)) return false;
  return /safari/.test(ua);
};

const DEVICE_ID_KEY = 'gj_device_id';

export const getOrCreateDeviceId = (): string => {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  if (!deviceId) {
    deviceId = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
};

export type NotificationPlatform = 'web' | 'ios_web' | 'android_web';

export const getPlatform = (): NotificationPlatform => {
  if (isIos()) return 'ios_web';
  if (isAndroid()) return 'android_web';
  return 'web';
};

/**
 * Checks if the current device is running Android
 * @returns true if the user agent contains Android
 */
export const isAndroid = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return /android/i.test(navigator.userAgent || '');
};

/**
 * Checks if the current screen size is mobile (less than 768px width)
 * @returns true if screen width is less than 768px
 */
export const isMobileScreenSize = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 768;
};

/**
 * Checks if we're on a mobile device with mobile screen size
 * @returns true if it's a mobile device and mobile screen size
 */
export const isMobileDevice = (): boolean => {
  // Prefer OS detection; fallback to touch capability + screen size
  if (isIos() || isAndroid()) return isMobileScreenSize();
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
  const maxTouch = (navigator as any).maxTouchPoints || 0;
  const hasTouch = maxTouch > 0;
  return hasTouch && isMobileScreenSize();
};

/**
 * Detect Chrome (or Chrome-like) browsers. Excludes Edge/Opera/Samsung.
 */
export const isChrome = (): boolean => {
  if (typeof navigator === 'undefined') return false;

  // Prefer User-Agent Client Hints when available
  const uaData = (navigator as any).userAgentData;
  if (uaData && Array.isArray(uaData.brands)) {
    const brands: Array<{ brand: string; version: string }> = uaData.brands;
    const isChromiumBrand = brands.some((b) => /chrome/i.test(b.brand));
    const ua = (navigator.userAgent || '').toLowerCase();
    if (/edg|opr|samsungbrowser|fxios/.test(ua)) return false;
    return isChromiumBrand;
  }

  const ua = (navigator.userAgent || '').toLowerCase();
  if (/edg|opr|samsungbrowser|fxios/.test(ua)) return false;
  return /chrome|crios/.test(ua);
};